// Designation privileges: tree of Module -> Sub-module (page) -> Options (actions).
// Super Admin picks a designation (e.g. "State Manager") and ticks what it may open and do.
// Users get these privileges through Assign Designation.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import BadgeIcon from '@mui/icons-material/Badge';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LockIcon from '@mui/icons-material/Lock';
import axios from 'axios';
import { API_BASE_URL } from '../../../constants/constants';
import BackButton from '../../../components/BackButton';
import { usePermissions } from '../../../context/PermissionContext';

const authHeaders = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } });

const sameSet = (a, b) => a.size === b.size && [...a].every((k) => b.has(k));

// All keys (page + options) of one sub-module / module
const subKeys = (sub) => [sub.key, ...sub.options.map((o) => o.key)];
const moduleKeys = (mod) => mod.subModules.flatMap(subKeys);

const DesignationPrivileges = () => {
  const { superAdmin, can, refresh: refreshMyPermissions } = usePermissions();
  const canEdit = can('master.designationPrivileges.edit');

  const [tree, setTree] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [postSearch, setPostSearch] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [saved, setSaved] = useState(new Set());
  const [draft, setDraft] = useState(new Set());
  const [search, setSearch] = useState('');
  const [grantedOnly, setGrantedOnly] = useState(false);
  const [open, setOpen] = useState({});
  const [saving, setSaving] = useState(false);
  const [pendingSwitch, setPendingSwitch] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const notify = (message, severity = 'success') => setToast({ open: true, message, severity });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [treeRes, postsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/permissions/tree`, authHeaders()),
        axios.get(`${API_BASE_URL}/permissions/posts`, authHeaders()),
      ]);
      setTree(treeRes.data.tree || []);
      setPosts(postsRes.data.posts || []);
    } catch (err) {
      console.error('Error loading privileges:', err);
      notify(err.response?.data?.message || 'Could not load designations', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const selectedPost = posts.find((p) => p._id === selectedId) || null;
  const dirty = !!selectedPost && !sameSet(saved, draft);

  // Keys the editor may change (Super Admin: all; others: only keys they hold)
  const editable = useCallback((key) => canEdit && (superAdmin || can(key)), [canEdit, superAdmin, can]);

  const selectPost = (id) => {
    const post = posts.find((p) => p._id === id);
    const keys = new Set(post ? post.permissions : []);
    setSelectedId(id);
    setSaved(keys);
    setDraft(new Set(keys));
    // open the modules that have something granted
    const nextOpen = {};
    tree.forEach((m) => {
      nextOpen[m.key] = moduleKeys(m).some((k) => keys.has(k));
    });
    setOpen(nextOpen);
  };

  const requestSelectPost = (id) => {
    if (id === selectedId) return;
    if (dirty) setPendingSwitch(id);
    else selectPost(id);
  };

  // ---- toggling ----
  const update = (fn) => setDraft((prev) => {
    const next = new Set(prev);
    fn(next);
    return next;
  });

  const togglePage = (sub, checked) => update((next) => {
    if (checked) {
      if (editable(sub.key)) next.add(sub.key);
    } else {
      subKeys(sub).forEach((k) => editable(k) && next.delete(k));
    }
  });

  const toggleOption = (sub, option, checked) => update((next) => {
    if (!editable(option.key)) return;
    if (checked) {
      next.add(option.key);
      next.add(sub.key); // an action needs its page
    } else {
      next.delete(option.key);
    }
  });

  const setKeys = (keys, checked) => update((next) => {
    keys.forEach((k) => {
      if (!editable(k)) return;
      if (checked) next.add(k);
      else next.delete(k);
    });
  });

  const copyFrom = (postId) => {
    const src = posts.find((p) => p._id === postId);
    if (!src) return;
    update((next) => {
      tree.forEach((m) => moduleKeys(m).forEach((k) => {
        if (!editable(k)) return;
        if (src.permissions.includes(k)) next.add(k);
        else next.delete(k);
      }));
    });
    notify(`Copied privileges from "${src.postName}". Review and save.`, 'info');
  };

  const save = async () => {
    if (!selectedPost) return;
    setSaving(true);
    try {
      const res = await axios.put(
        `${API_BASE_URL}/permissions/posts/${selectedPost._id}`,
        { permissions: [...draft] },
        authHeaders(),
      );
      const keys = res.data.permissions || [...draft];
      setPosts((prev) => prev.map((p) => (p._id === selectedPost._id ? { ...p, permissions: keys, migrated: true } : p)));
      setSaved(new Set(keys));
      setDraft(new Set(keys));
      refreshMyPermissions();
      notify(`Privileges of "${selectedPost.postName}" saved`);
    } catch (err) {
      console.error('Error saving privileges:', err);
      notify(err.response?.data?.message || 'Could not save privileges', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ---- filtering ----
  const q = search.trim().toLowerCase();
  const visibleTree = useMemo(() => tree
    .map((mod) => {
      const modHit = !q || mod.label.toLowerCase().includes(q);
      const subModules = mod.subModules.filter((sub) => {
        if (grantedOnly && !subKeys(sub).some((k) => draft.has(k))) return false;
        if (modHit) return true;
        return sub.label.toLowerCase().includes(q)
          || (sub.path || '').toLowerCase().includes(q)
          || sub.options.some((o) => o.label.toLowerCase().includes(q));
      });
      return { ...mod, subModules };
    })
    .filter((mod) => mod.subModules.length > 0), [tree, q, grantedOnly, draft]);

  const expandAll = (value) => {
    const next = {};
    tree.forEach((m) => { next[m.key] = value; });
    setOpen(next);
  };

  const allKeys = useMemo(() => tree.flatMap(moduleKeys), [tree]);

  const totals = useMemo(() => {
    let pages = 0; let actions = 0; let allPages = 0; let allActions = 0;
    tree.forEach((m) => m.subModules.forEach((s) => {
      allPages += 1;
      allActions += s.options.length;
      if (draft.has(s.key)) pages += 1;
      s.options.forEach((o) => { if (draft.has(o.key)) actions += 1; });
    }));
    return { pages, actions, allPages, allActions };
  }, [tree, draft]);

  const filteredPosts = posts.filter((p) => p.postName.toLowerCase().includes(postSearch.trim().toLowerCase()));

  const pageCount = (post) => {
    const keys = new Set(post.permissions);
    let n = 0;
    tree.forEach((m) => m.subModules.forEach((s) => { if (keys.has(s.key)) n += 1; }));
    return n;
  };

  // ---- render helpers ----
  const triState = (keys) => {
    const n = keys.filter((k) => draft.has(k)).length;
    return { checked: n > 0 && n === keys.length, indeterminate: n > 0 && n < keys.length, count: n };
  };

  const renderSub = (sub) => {
    const pageOn = draft.has(sub.key);
    const all = triState(subKeys(sub));
    const lockedPage = !editable(sub.key);
    return (
      <Box
        key={sub.key}
        sx={{
          px: 2,
          py: 1.25,
          borderTop: '1px solid #f1f5f9',
          bgcolor: pageOn ? '#fffbf2' : 'transparent',
          transition: 'background-color .15s',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Checkbox
            size="small"
            checked={pageOn}
            disabled={lockedPage}
            onChange={(e) => togglePage(sub, e.target.checked)}
            inputProps={{ 'aria-label': `Open ${sub.label}` }}
          />
          <Box sx={{ flex: 1, minWidth: 180 }}>
            <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 0.75 }}>
              {sub.label}
              {lockedPage && canEdit && (
                <Tooltip title="You can't change a privilege you don't have">
                  <LockIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                </Tooltip>
              )}
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: '#94a3b8', fontFamily: 'monospace' }}>{sub.path}</Typography>
          </Box>
          {sub.options.length > 0 && (
            <Button
              size="small"
              disabled={!canEdit}
              onClick={() => setKeys(subKeys(sub), !all.checked)}
              sx={{ textTransform: 'none', fontSize: 12, color: '#64748b' }}
            >
              {all.checked ? 'Clear page' : 'All actions'}
            </Button>
          )}
        </Box>
        {sub.options.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, pl: 5.5, pt: 0.75 }}>
            {sub.options.map((o) => {
              const on = draft.has(o.key);
              const locked = !editable(o.key);
              return (
                <Chip
                  key={o.key}
                  size="small"
                  label={o.label}
                  icon={o.danger ? <WarningAmberIcon sx={{ fontSize: 15 }} /> : undefined}
                  onClick={locked ? undefined : () => toggleOption(sub, o, !on)}
                  variant={on ? 'filled' : 'outlined'}
                  color={on ? 'success' : 'default'}
                  sx={{
                    fontSize: 12,
                    fontWeight: on ? 600 : 500,
                    cursor: locked ? 'not-allowed' : 'pointer',
                    opacity: locked ? 0.55 : 1,
                    borderStyle: on ? 'solid' : 'dashed',
                    '& .MuiChip-icon': { color: on ? '#fde68a' : '#f59e0b' },
                  }}
                />
              );
            })}
          </Box>
        )}
      </Box>
    );
  };

  const renderModule = (mod) => {
    const fullMod = tree.find((m) => m.key === mod.key) || mod;
    const state = triState(moduleKeys(fullMod));
    const pagesOn = fullMod.subModules.filter((s) => draft.has(s.key)).length;
    const isOpen = !!open[mod.key] || !!q || grantedOnly;
    return (
      <Paper
        key={mod.key}
        variant="outlined"
        sx={{
          mb: 1.5,
          borderRadius: 2.5,
          overflow: 'hidden',
          borderColor: state.count > 0 ? '#fcd9a0' : '#e2e8f0',
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5, bgcolor: state.count > 0 ? '#fff7e6' : '#f8fafc', cursor: 'pointer' }}
          onClick={() => setOpen((prev) => ({ ...prev, [mod.key]: !isOpen }))}
        >
          <IconButton size="small" aria-label={isOpen ? 'Collapse' : 'Expand'}>
            {isOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />}
          </IconButton>
          <Checkbox
            checked={state.checked}
            indeterminate={state.indeterminate}
            disabled={!canEdit}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setKeys(moduleKeys(fullMod), e.target.checked)}
            inputProps={{ 'aria-label': `Select all of ${mod.label}` }}
          />
          <Typography sx={{ fontWeight: 700, fontSize: 15, flex: 1, color: '#0f172a' }}>{mod.label}</Typography>
          <Chip
            size="small"
            label={`${pagesOn}/${fullMod.subModules.length} pages`}
            sx={{ mr: 1, fontWeight: 600, bgcolor: pagesOn ? '#FFA500' : '#e2e8f0', color: pagesOn ? '#fff' : '#64748b' }}
          />
        </Box>
        <Collapse in={isOpen} unmountOnExit>
          {mod.subModules.map(renderSub)}
        </Collapse>
      </Paper>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <BackButton />
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>Designation Privileges</Typography>
          <Typography sx={{ color: '#64748b', fontSize: 14 }}>
            Choose a designation, then tick the modules, pages and actions it may use. Users get them through Assign Designation.
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '300px 1fr' }, gap: 2.5, alignItems: 'start' }}>
        {/* Designations */}
        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', position: { md: 'sticky' }, top: { md: 16 } }}>
          <Box sx={{ p: 2, pb: 1.5 }}>
            <Typography sx={{ fontWeight: 700, mb: 1.25 }}>Designations</Typography>
            <TextField
              size="small"
              fullWidth
              placeholder="Search designation"
              value={postSearch}
              onChange={(e) => setPostSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />
          </Box>
          <Divider />
          <List dense sx={{ maxHeight: { md: 'calc(100vh - 260px)' }, overflowY: 'auto', py: 0 }}>
            {filteredPosts.length === 0 && (
              <Typography sx={{ p: 2, color: '#94a3b8', fontSize: 13 }}>
                No designations. Create one in Master → Manage Designation.
              </Typography>
            )}
            {filteredPosts.map((p) => (
              <ListItemButton
                key={p._id}
                selected={p._id === selectedId}
                onClick={() => requestSelectPost(p._id)}
                sx={{ py: 1.25, '&.Mui-selected': { bgcolor: '#fff3dc', borderLeft: '4px solid #FFA500' } }}
              >
                <BadgeIcon sx={{ mr: 1.5, color: p._id === selectedId ? '#FFA500' : '#94a3b8' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography noWrap sx={{ fontWeight: 600, fontSize: 14 }}>{p.postName}</Typography>
                  <Typography sx={{ fontSize: 12, color: '#64748b' }}>
                    {pageCount(p)} pages{p.status !== 'active' ? ' · inactive' : ''}
                  </Typography>
                </Box>
              </ListItemButton>
            ))}
          </List>
        </Paper>

        {/* Tree */}
        <Box>
          {!selectedPost ? (
            <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: 3, borderStyle: 'dashed' }}>
              <BadgeIcon sx={{ fontSize: 48, color: '#cbd5e1' }} />
              <Typography sx={{ fontWeight: 700, mt: 1 }}>Select a designation</Typography>
              <Typography sx={{ color: '#64748b', fontSize: 14 }}>Its modules, pages and actions appear here.</Typography>
            </Paper>
          ) : (
            <>
              <Paper
                variant="outlined"
                sx={{ p: 2, mb: 2, borderRadius: 3, position: 'sticky', top: 0, zIndex: 2, bgcolor: '#fff' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: 18 }}>
                      {selectedPost.postName}
                      {dirty && <Chip size="small" label="Unsaved changes" color="warning" sx={{ ml: 1, fontWeight: 600 }} />}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: '#64748b' }}>
                      {totals.pages} of {totals.allPages} pages · {totals.actions} of {totals.allActions} actions
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    startIcon={<UndoIcon />}
                    disabled={!dirty || saving}
                    onClick={() => setDraft(new Set(saved))}
                    sx={{ textTransform: 'none' }}
                  >
                    Discard
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    disabled={!dirty || saving || !canEdit}
                    onClick={save}
                    sx={{ textTransform: 'none', fontWeight: 700, color: '#fff' }}
                  >
                    Save privileges
                  </Button>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2, flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    placeholder="Search module, page or action"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ flex: 1, minWidth: 280 }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                  />
                  <FormControlLabel
                    control={<Switch size="small" checked={grantedOnly} onChange={(e) => setGrantedOnly(e.target.checked)} />}
                    label={<Typography sx={{ fontSize: 13 }}>Granted only</Typography>}
                  />
                  <Tooltip title="Expand all"><IconButton onClick={() => expandAll(true)}><UnfoldMoreIcon /></IconButton></Tooltip>
                  <Tooltip title="Collapse all"><IconButton onClick={() => expandAll(false)}><UnfoldLessIcon /></IconButton></Tooltip>
                  <Button size="small" disabled={!canEdit} onClick={() => setKeys(allKeys, true)} sx={{ textTransform: 'none' }}>Select all</Button>
                  <Button size="small" disabled={!canEdit} onClick={() => setKeys(allKeys, false)} sx={{ textTransform: 'none' }} color="error">Clear all</Button>
                  <Select
                    size="small"
                    displayEmpty
                    value=""
                    disabled={!canEdit}
                    onChange={(e) => copyFrom(e.target.value)}
                    sx={{ minWidth: 190, fontSize: 13 }}
                    renderValue={() => 'Copy from designation…'}
                  >
                    {posts.filter((p) => p._id !== selectedId).map((p) => (
                      <MenuItem key={p._id} value={p._id}>{p.postName}</MenuItem>
                    ))}
                  </Select>
                </Box>
              </Paper>

              {!canEdit && (
                <Alert severity="info" sx={{ mb: 2 }}>You can view these privileges but not change them.</Alert>
              )}
              {!selectedPost.migrated && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  These privileges were converted from the old numbered system. Check them and press Save once to keep them in the new format.
                </Alert>
              )}

              <Box sx={{ display: 'flex', gap: 2.5, mb: 1.5, color: '#64748b', fontSize: 12.5, flexWrap: 'wrap', alignItems: 'center' }}>
                <span>Checkbox = can open the page</span>
                <span>Green chip = allowed action</span>
                <span>
                  <WarningAmberIcon sx={{ fontSize: 14, verticalAlign: 'middle', color: '#f59e0b', mr: 0.5 }} />
                  Sensitive action (money, blocking, settings)
                </span>
              </Box>

              {visibleTree.length === 0 ? (
                <Typography sx={{ color: '#94a3b8', p: 3, textAlign: 'center' }}>Nothing matches.</Typography>
              ) : (
                visibleTree.map(renderModule)
              )}
            </>
          )}
        </Box>
      </Box>

      <Dialog open={!!pendingSwitch} onClose={() => setPendingSwitch(null)}>
        <DialogTitle>Discard unsaved changes?</DialogTitle>
        <DialogContent>
          <Typography>Changes to "{selectedPost?.postName}" are not saved yet.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingSwitch(null)}>Keep editing</Button>
          <Button
            color="error"
            onClick={() => {
              const id = pendingSwitch;
              setPendingSwitch(null);
              selectPost(id);
            }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} variant="filled" onClose={() => setToast((t) => ({ ...t, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DesignationPrivileges;
